import moment from "moment-timezone";

import type {MessageRule} from "../models/messageRule";

export function generateMinutesFormatted(): Record<number, string> {
    const options: Record<number, string> = {};
    const times = generateTime();
    for (let i = 0; i < times.length; i += 1) {
        const minutes = times[i].value;
        options[minutes] =
            Math.floor(minutes / 60) > 0
                ? `${Math.floor(minutes / 60)} hours and ${minutes % 60} minutes`
                : `${minutes} minutes`;
    }
    return options;
}

export function generateTime() {
    const opts: {value: number; label: string}[] = [];

    // Loop through 24 hours
    for (let hour = 0; hour < 24; hour += 1) {
        // For each hour, loop through 60 minutes
        for (let minute = 0; minute < 60; minute += 1) {
            // Calculate total minutes for value (hour * 60 + minutes)
            const totalMinutes = hour * 60 + minute;
            const {time, amPM} = formatMinutes(totalMinutes);

            opts.push({
                value: totalMinutes,
                label: `${time}${amPM}`
            });
        }
    }
    return opts;
}

export function formatMinutes(minutes: number) {
    const time = moment().startOf("day").add(minutes, "minutes");
    return {
        time: time.format("h:mm"),
        amPM: time.format("a")
    };
}

export function formatHours(hours: number) {
    const time = moment().startOf("day").add(hours, "hours");
    return {
        time: time.format("h"),
        amPM: time.format("a")
    };
}

export function generateMinutes() {
    const opts: {value: number; label: string}[] = [];
    // Loop through 60 minutes
    for (let minute = 0; minute < 60; minute += 1) {
        opts.push({
            value: minute,
            label: minute.toString()
        });
    }
    return opts;
}

export function getFormattedEvent(event: MessageRule["event"]): string {
    let formattedEvent: string = event;
    if (event === "timedout") {
        formattedEvent = "Expired Pre-Approval";
    } else if (event === "pending") {
        formattedEvent = "Booking Request";
    } else if (event === "booking") {
        formattedEvent = "Confirmed Booking";
    } else if (event === "checkin") {
        formattedEvent = "Check-In";
    } else if (event === "occupied") {
        formattedEvent = "During the Reservation";
    } else if (event === "checkout") {
        formattedEvent = "Check-Out";
    } else if (event === "doorUnlocked") {
        formattedEvent = "Door Was Unlocked";
    } else if (event === "checkinChanged") {
        formattedEvent = "Check-In Date Changed";
    } else if (event === "checkoutChanged") {
        formattedEvent = "Check-Out Date Changed";
    } else if (event === "guestsChanged") {
        formattedEvent = "Number of Guests Changed";
    } else if (event === "petsChanged") {
        formattedEvent = "Number of Pets Changed";
    } else if (event === "cancelled") {
        formattedEvent = "Canceled Booking";
    } else {
        formattedEvent = formattedEvent[0].toUpperCase() + formattedEvent.slice(1).toLowerCase();
    }

    return formattedEvent;
}

export function getLanguageText(rule: MessageRule): string {
    let languageText = "";
    if (rule.languagesEnabled) {
        let languages: string[];
        if (rule.reviewEnabled && rule.reviewMessages) {
            languages = Object.keys(rule.reviewMessages);
        } else if (rule.messages) {
            languages = Object.keys(rule.messages);
        } else {
            languages = [];
        }
        languages = languages.filter(language => {
            return language !== "default";
        });
        languageText = languages.join(", ").toLocaleUpperCase();
    }

    return languageText;
}

export function getLimitDaysFormatted(limitDays: MessageRule["limitDays"]): string {
    return Object.keys(limitDays)
        .reduce((result, day) => {
            if (limitDays && limitDays[day]) {
                result.push(day.charAt(0).toUpperCase() + day.substring(1, 2));
            }
            return result;
        }, [] as string[])
        .join(", ");
}

export function getDelayFormatted(delay: MessageRule["delay"]): string {
    if (!delay) {
        return "";
    }
    if (delay < 60) {
        return `${delay} min`;
    }
    if (delay >= 60 && delay < 1440) {
        return `${delay / 60} hour`;
    }
    if (delay >= 1440) {
        return `${delay / 1440} day`;
    }
    return "";
}

export function getMessageRuleMessage(rule: MessageRule): string {
    return typeof rule.messages !== "object" && rule.message
        ? rule.message
        : typeof rule.messages === "object" && rule.messages.default
        ? rule.messages.default
        : "";
}

export function getMessageRuleReviewMessage(rule: MessageRule): string {
    if (rule.reviewMessage === "" && rule.reviewMessages.default) {
        return rule.reviewMessages.default;
    }

    return rule.reviewMessage || "";
}

export function getMessageRuleLastMinuteMessage(rule: MessageRule): string {
    if (rule.lastMinuteMessage === "" && rule.lastMinuteMessages.default) {
        return rule.lastMinuteMessages.default;
    }

    return rule.lastMinuteMessage || "";
}
